import { createContext, useContext, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import authService from '../../api/services/authService';
import { mergeGuestCart } from '../../api/services/cartService';
import { setCustomerType } from '../../api/services/analyticsService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const queryClient = useQueryClient();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadUser = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;

                // Quick client-side expiry check — with 15-min tokens this fires
                // more frequently, so attempt a silent refresh before giving up.
                try {
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    if (payload.exp * 1000 < Date.now()) {
                        try {
                            const data = await authService.refresh();
                            localStorage.setItem('token', data.token);
                            // Token refreshed — continue below to load profile
                        } catch {
                            await authService.logout();
                            queryClient.clear();
                            setCustomerType('guest');
                            return;
                        }
                    }
                } catch {
                    // Malformed token — let the server reject it
                }

                const userData = await authService.getProfile();
                setUser(userData);
                // Keep analyticsService in sync with the loaded user segment.
                setCustomerType(userData?.customerType ?? 'retail');
            } catch (error) {
                console.error('Error loading user:', error);
                await authService.logout();
                queryClient.clear();
                setCustomerType('guest');
            } finally {
                setLoading(false);
            }
        };

        loadUser();
    }, [queryClient]);

    const login = async (credentials) => {
        const data = await authService.login(credentials);
        localStorage.setItem('token', data.token);
        const { token, ...userData } = data;
        queryClient.clear();
        await mergeGuestCart();
        setUser(userData);
        setCustomerType(userData?.customerType ?? 'retail');
        return userData;
    };

    const register = async (registrationData) => {
        const data = await authService.register(registrationData);
        localStorage.setItem('token', data.token);
        const { token, ...userData } = data;
        queryClient.clear();
        await mergeGuestCart();
        setUser(userData);
        setCustomerType(userData?.customerType ?? 'retail');
        return userData;
    };

    const logout = async () => {
        await authService.logout();
        queryClient.clear();
        setUser(null);
        setCustomerType('guest');
    };

    const updateUser = (userData) => {
        setUser(userData);
        setCustomerType(userData?.customerType ?? 'retail');
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
